import React from "react";
import { Link, useLocation } from "react-router-dom";

interface PageHeaderProps {
  title: string;
  subTitle?: string;
}

// Helper to format path segment into readable label
const formatSegment = (segment: string): string => {
  // Handle special cases
  if (segment === "new") return "Create";
  // Convert kebab-case to Title Case
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const PageHeader: React.FC<PageHeaderProps> = ({ title, subTitle }) => {
  const location = useLocation();

  // Build dynamic breadcrumbs from all path segments
  const pathSegments = location.pathname.split("/").filter(Boolean);

  return (
    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start gap-3 mb-3">
      <div>
        {subTitle && (
          <p className="mb-1 small fw-semibold text-uppercase text-secondary">
            {subTitle}
          </p>
        )}
        <h1 className="h2 mb-0 fw-semibold text-dark">{title}</h1>
      </div>

      <nav aria-label="breadcrumb" className="ms-lg-auto">
        <ol className="breadcrumb small mb-0 justify-content-lg-end">
          <li className="breadcrumb-item">
            <Link to="/dashboard" className="text-decoration-none text-body-secondary">
              <i className="bi bi-speedometer2 me-1"></i> Home
            </Link>
          </li>
        {pathSegments.map((segment, index) => {
          // Stop at 'new' segment and make it active (don't show route param after it)
          const isNewSegment = segment === "new";
          const isLast = index === pathSegments.length - 1 || isNewSegment;
          const path = "/" + pathSegments.slice(0, index + 1).join("/");
          const label = formatSegment(segment);

          // Skip segments after 'new' (e.g., the type parameter)
          if (index > 0 && pathSegments[index - 1] === "new") {
            return null;
          }

          return isLast ? (
            <li key={segment} className="breadcrumb-item active text-muted" aria-current="page">
              {label}
            </li>
          ) : (
            <li key={segment} className="breadcrumb-item">
              <Link to={path} className="text-decoration-none text-body-secondary">{label}</Link>
            </li>
          );
        })}
        </ol>
      </nav>
    </div>
  );
};

export default PageHeader;
